import React from 'react';
import AnalysisActionButtons from '../layout/AnalysisActionButtons';
import AnalysisHistoryList from '../layout/AnalysisHistoryList';
import CurrentAnalysisNotes from '../ui/CurrentAnalysisNotes';
import SidebarAnalysisForm from '../ui/SidebarAnalysisForm';

/**
 * ChatSettingsView - Analysis settings integrated as a chat tab
 * Reuses components from AnalysisSettingsBar but in chat-friendly layout
 */
const ChatSettingsView = ({
  // Essential props
  hasAnalysis,
  darkMode,
  isDemoUser,
  
  // Callback props
  onSave,
  onReset,
  onLoadSavedAnalysis,
  
  // State props
  isViewingSavedAnalysis = false,
  refreshTrigger = 0,
  
  // Analysis data
  temporaryAnalysis = null,
  
  // History-related props
  onRunAgain,
  isAnalysisRunning = false,
  
  // Analysis flow props
  progressiveAnalysisState = null,
  onRunNewAnalysis,
  
  // Internal state management
  showSidebarForm = false,
  onShowSidebarForm,
  onHideSidebarForm,
}) => {
  
  // Handle new analysis form submission
  const handleSidebarFormSubmit = async (formData) => {
    // Helper function to format Date objects to YYYY-MM-DD strings
    const formatDateForBackend = (date) => {
      if (!date) return null;
      if (typeof date === 'string') return date; // Already formatted
      const d = new Date(date);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    // Convert sidebar form data to analysis parameters format
    const analysisParams = {
      dates: {
        formattedDates: {
          old_period: `${formatDateForBackend(formData.dates.oldStartDate)} - ${formatDateForBackend(formData.dates.oldEndDate)}`,
          new_period: `${formatDateForBackend(formData.dates.newStartDate)} - ${formatDateForBackend(formData.dates.newEndDate)}`
        }
      },
      country: formData.country || '',
      useCountryScaling: formData.useCountryScaling || false
    };
    
    try {
      // Call the analysis handler
      await onRunNewAnalysis(analysisParams);
      
      // Close the form on success
      onHideSidebarForm();
    } catch (error) {
      // Form will show the error from progressiveAnalysisState
    }
  };

  // Handle form cancel
  const handleSidebarFormCancel = () => {
    onHideSidebarForm();
  };

  // Handle new analysis button click
  const handleNewAnalysisClick = () => {
    onShowSidebarForm();
  };

  // If there's no analysis, show a message
  if (!hasAnalysis) {
    return (
      <div className={`flex-1 flex items-center justify-center p-6 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
        <div className="text-center">
          <svg className="w-12 h-12 mx-auto mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.343 3.94c.09-.542.56-.94 1.11-.94h1.093c.55 0 1.02.398 1.11.94l.149.894c.07.424.384.764.78.93.398.164.855.142 1.205-.108l.737-.527a1.125 1.125 0 011.45.12l.773.774c.39.389.44 1.002.12 1.45l-.527.737c-.25.35-.272.806-.107 1.204.165.397.505.71.93.78l.893.15c.543.09.94.56.94 1.109v1.094c0 .55-.397 1.02-.94 1.11l-.893.149c-.425.07-.765.383-.93.78-.165.398-.143.854.107 1.204l.527.738c.32.447.269 1.06-.12 1.45l-.774.773a1.125 1.125 0 01-1.449.12l-.738-.527c-.35-.25-.806-.272-1.203-.107-.397.165-.71.505-.781.929l-.149.894c-.09.542-.56.94-1.11.94h-1.094c-.55 0-1.019-.398-1.11-.94l-.148-.894c-.071-.424-.384-.764-.781-.93-.398-.164-.854-.142-1.204.108l-.738.527c-.447.32-1.06.269-1.45-.12l-.773-.774a1.125 1.125 0 01-.12-1.45l.527-.737c.25-.35.273-.806.108-1.204-.165-.397-.505-.71-.93-.78l-.894-.15c-.542-.09-.94-.56-.94-1.109v-1.094c0-.55.398-1.02.94-1.11l.894-.149c.424-.07.765-.383.93-.78.165-.398.143-.854-.107-1.204l-.527-.738a1.125 1.125 0 01.12-1.45l.773-.773a1.125 1.125 0 011.45-.12l.737.527c.35.25.807.272 1.204.107.397-.165.71-.505.78-.929l.15-.894z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <p className="text-sm">No analysis available</p>
          <p className="text-xs mt-1">Run an analysis to access settings</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Show Sidebar Form when active */}
      {showSidebarForm ? (
        <div className="flex-1 overflow-y-auto p-4">
          <SidebarAnalysisForm
            onSubmit={handleSidebarFormSubmit}
            onCancel={handleSidebarFormCancel}
            analysisFlow={{
              isRunning: Boolean(
                progressiveAnalysisState && 
                progressiveAnalysisState.stage !== 'idle' &&
                progressiveAnalysisState.stage !== 'complete' &&
                progressiveAnalysisState.stage !== 'error' &&
                !progressiveAnalysisState.fullAnalysisComplete &&
                (progressiveAnalysisState.stage === 'loading' || 
                 progressiveAnalysisState.stage === 'summary' || 
                 progressiveAnalysisState.stage === 'polling' ||
                 (progressiveAnalysisState.progress > 0 && progressiveAnalysisState.progress < 100))
              ),
              message: progressiveAnalysisState?.message || '',
              error: progressiveAnalysisState?.error || '',
              progress: progressiveAnalysisState?.progress || 0
            }}
            darkMode={darkMode}
            disabled={Boolean(
              progressiveAnalysisState && 
              progressiveAnalysisState.stage !== 'idle' &&
              progressiveAnalysisState.stage !== 'complete' &&
              progressiveAnalysisState.stage !== 'error' &&
              !progressiveAnalysisState.fullAnalysisComplete &&
              (progressiveAnalysisState.stage === 'loading' || 
               progressiveAnalysisState.stage === 'summary' || 
               progressiveAnalysisState.stage === 'polling' ||
               (progressiveAnalysisState.progress > 0 && progressiveAnalysisState.progress < 100))
            )}
          />
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto">
          {/* Action Buttons Section */}
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <AnalysisActionButtons
              onSave={onSave}
              onReset={onReset}
              hasAnalysis={hasAnalysis}
              isViewingSavedAnalysis={isViewingSavedAnalysis}
              darkMode={darkMode}
              isAnalysisRunning={isAnalysisRunning}
              onActionComplete={() => {}} // No need to close anything in chat mode
              onNewAnalysis={handleNewAnalysisClick}
            />
          </div>

          {/* Analysis Notes Section */}
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
              Analysis Notes
            </h3>
            <CurrentAnalysisNotes
              analysisParams={temporaryAnalysis?.meta}
              temporaryAnalysis={temporaryAnalysis}
              darkMode={darkMode}
            />
          </div>

          {/* Analysis History Section */}
          <div className="p-4">
            <h3 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
              Analysis History
            </h3>
            <AnalysisHistoryList
              onRunAgain={onRunAgain}
              onActionComplete={() => {}} // No need to close anything in chat mode
              shouldFetch={true} // Always fetch since we're in a tab
              isDemoUser={isDemoUser}
              darkMode={darkMode}
              refreshTrigger={refreshTrigger}
              isAnalysisRunning={isAnalysisRunning}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatSettingsView;

